
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import ESPWiFiSetupGuide from "@/components/ESPWiFiSetupGuide";
import OTABuildGuide from "@/components/OTABuildGuide";
import { Separator } from "@/components/ui/separator";
import { Wifi, LinkIcon, RefreshCw, Upload } from "lucide-react";
import { Navigation } from "@/components/Navigation";

const DeviceSetup = () => {
  const [activeTab, setActiveTab] = useState("wifi-setup");

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container max-w-5xl py-6 space-y-6">
        <div className="flex items-center mb-4">
          <span className="text-3xl font-bold tracking-tight">Device Setup</span>
          <Button variant="outline" size="sm" className="gap-1 ml-auto">
            <RefreshCw className="h-4 w-4" />
            Refresh Status
          </Button>
        </div>
        
        <Separator />
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full md:w-auto grid-cols-3">
            <TabsTrigger value="wifi-setup" className="flex gap-2">
              <Wifi className="h-4 w-4" />
              WiFi Setup
            </TabsTrigger>
            <TabsTrigger value="master-slave" className="flex gap-2">
              <LinkIcon className="h-4 w-4" />
              Master-Slave Pairing
            </TabsTrigger>
            <TabsTrigger value="ota-build" className="flex gap-2">
              <Upload className="h-4 w-4" />
              Build OTA File
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="wifi-setup" className="space-y-4 mt-4">
            <ESPWiFiSetupGuide />
            
            <Card>
              <CardHeader>
                <CardTitle>Technical Details</CardTitle>
                <CardDescription>
                  How the WiFi setup works behind the scenes
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <p>
                  When first powered on, your ESP32 device creates its own WiFi access point named "ESP32-Setup". 
                  Once you connect to this network and open a web browser, a captive portal redirects you to the 
                  configuration page where you can enter your home WiFi credentials.
                </p>
                <p className="mt-3">
                  After saving credentials, the ESP32 will attempt to connect to your network. If successful, 
                  it remembers these settings even after power cycles. If the connection fails, it will 
                  revert to setup mode automatically.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="master-slave" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Master-Slave Pairing</CardTitle>
                <CardDescription>
                  Connect your controller (master) to LED devices (slaves)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  The master-slave pairing feature is coming soon. This will allow you to automatically connect 
                  your main controller device to multiple LED controllers without manual configuration.
                </p>
                <p>
                  Automatic pairing uses ESP-NOW technology for direct, low-latency communication between devices,
                  ensuring your light patterns respond instantly to triggers.
                </p>
                
                <div className="bg-muted p-4 rounded-md mt-4">
                  <h4 className="font-medium mb-2">Available in next update:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Auto-discovery of slave devices</li>
                    <li>One-button pairing process</li>
                    <li>Status monitoring of connected devices</li>
                    <li>Pattern synchronization across multiple devices</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ota-build" className="space-y-4 mt-4">
            <OTABuildGuide />

            <Card>
              <CardHeader>
                <CardTitle>Why Build a .bin File?</CardTitle>
                <CardDescription>
                  The difference between flashing over USB and updating over the air
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <p>
                  When you click <strong>Build</strong> in PlatformIO, the compiler turns the C++ code into a single binary
                  file (<code>.bin</code>). That file contains everything the ESP32 needs to run.
                </p>
                <p>
                  Flashing over USB writes that file directly to the board. OTA does the same thing, but the file travels
                  through your WiFi network from the dashboard to the device. Because the dashboard stores the file,
                  you can update many devices at once without plugging a cable into each one.
                </p>
                <p>
                  Always make sure the <strong>Type</strong> and <strong>Series</strong> you select in the dashboard match
                  the devices you want to update, so a master file is never sent to a slave and vice versa.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DeviceSetup;
